import { Select } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { Project } from '../../models/Project';
import { Projects } from '../../api/Projects';
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import AppLoader from '../common/AppLoader';

type Props = {
  assetId: number;
  assetType: 'application' | 'dataset' | 'model' | 'service';
  assetLabel?: string; // 'Application' | 'Dataset' | 'Model' | 'Service'
}

export default function AssetProjectsEditor({ assetId, assetType, assetLabel = "Asset" }: Props) {
  const [projects, setProjects] = useState<Project[]>();
  const [error, setError] = useState(false);
  const [assetProjects, setAssetProjects] = useState<number[]>([]);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const projectsOptions = useMemo(() => projects?.map(p => ({ label: p.name, value: p.id, trailing: p.description })), [projects]);

  useEffect(() => {
    Projects.getProjects()
      .then(
        ({ data }) => setProjects(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('projects-error', message, `Projects Error ${status ? `(${status})` : ''}`, 'danger');
          setProjects([]);
          setError(true);
        }
      );

    Projects.getAssetProjects(assetType, assetId)
      .then(
        ({ data }) => setAssetProjects(data.map(p => p.id!)),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('asset-projects-error', message, `Asset Projects Error ${status ? `(${status})` : ''}`, 'danger');
          setAssetProjects([]);
          setError(true);
        }
      );
  }, [assetId, assetType, showToast]);

  const handleAddProject = (project: number) => {
    showLoader();

    Projects.addAsset(project, { id: assetId, type: assetType })
      .then(
        () => {
          setAssetProjects(curr => curr.concat(project));
          hideLoader();
          showToast('add-project-success', `${assetLabel} successfully added to project.`, `Add ${assetLabel} Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('add-project-error', message, `Add ${assetLabel} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const handleRemoveProject = (project: number) => {
    showLoader();

    Projects.removeAsset(project, { id: assetId, type: assetType })
      .then(
        () => {
          setAssetProjects((previous) => previous.filter((prev) => prev !== project));
          hideLoader();
          showToast('remove-project-success', `${assetLabel} successfully removed from project.`, `${assetLabel} Remove Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('remove-project-error', message, `${assetLabel} Remove Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleClearProjects = () => {
    showLoader();

    Promise.all(assetProjects.map(id => Projects.removeAsset(id!, { id: assetId, type: assetType })))
      .then(
        () => {
          setAssetProjects([]);
          hideLoader();
          showToast('remove-projects-success', `${assetLabel} successfully removed from all projects`, `${assetLabel} Remove Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('remove-projects-error', message, `${assetLabel} Remove Error ${status ? `(${status})` : ''}`, 'danger');
        })
  }

  if (error) return (
    <Alert variant="danger" className="py-2">
      <FontAwesomeIcon icon={solid('exclamation-circle')} />
      <span className="ms-2">An error occurred, try again later.</span>
    </Alert>
  )

  return projectsOptions
    ? (
      <>
        <Select
          mode="multiple"
          className="projects-select mb-3 w-100"
          placeholder="Select projects"
          value={assetProjects}
          onSelect={handleAddProject}
          onDeselect={handleRemoveProject}
          onClear={handleClearProjects}
          loading={!projectsOptions}
          filterOption={(search, option) => (option?.label as string || '').toLowerCase().includes(search?.toLowerCase())}
          allowClear
        >
          {projectsOptions?.map(option => (
            <Select.Option key={option.value} value={option.value} label={option.label}>
              <h6 className="mb-0">{option.label}</h6>
              <span className="text-muted">{option.trailing}</span>
            </Select.Option>
          ))}
        </Select>

        {assetProjects.length === 0 && (
          <Alert variant="info" className="m-0 py-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">No projects found for this {assetType}.</span>
          </Alert>
        )}
      </>
    )
    : <AppLoader className="py-3" justify="center" size={80} />
}